/**
 * @fileoverview Month event badge — the coloured pill on a month-view cell.
 *
 * Adapted: removed the bundled `DraggableEvent` + `EventDetailsDialog`
 * wrappers; clicking the badge now calls `onEventClick(event)` from
 * context so Morgan's KeyDate side panel can open instead.
 */

import { cva, type VariantProps } from "class-variance-authority";
import { endOfDay, format, isSameDay, parseISO, startOfDay } from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";

const eventBadgeVariants = cva(
  "mx-1 flex min-h-6 select-none items-start justify-between gap-1.5 rounded-md border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      color: {
        blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 [&_.event-dot]:fill-blue-600",
        green:
          "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300 [&_.event-dot]:fill-green-600",
        red: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 [&_.event-dot]:fill-red-600",
        yellow:
          "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 [&_.event-dot]:fill-yellow-600",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300 [&_.event-dot]:fill-purple-600",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300 [&_.event-dot]:fill-orange-600",
        gray: "border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 [&_.event-dot]:fill-neutral-600",
        pink: "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300 [&_.event-dot]:fill-pink-600",

        "blue-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-blue-600",
        "green-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-green-600",
        "red-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-red-600",
        "yellow-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-yellow-600",
        "purple-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-purple-600",
        "orange-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-orange-600",
        "gray-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-neutral-600",
        "pink-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-pink-600",
      },
      multiDayPosition: {
        first:
          "relative z-10 mr-0 w-[calc(100%_-_3px)] rounded-r-none border-r-0 [&>span]:mr-2.5",
        middle:
          "relative z-10 mx-0 w-[calc(100%_+_1px)] rounded-none border-x-0",
        last: "ml-0 rounded-l-none border-l-0",
        none: "",
      },
    },
    defaultVariants: {
      color: "blue-dot",
    },
  },
);

interface IProps {
  event: IEvent;
  cellDate: Date;
  eventCurrentDay?: number;
  eventTotalDays?: number;
  className?: string;
  position?: "first" | "middle" | "last" | "none";
}

export function MonthEventBadge({
  event,
  cellDate,
  eventCurrentDay,
  eventTotalDays,
  className,
  position: propPosition,
}: IProps) {
  const { badgeVariant, onEventClick, showJobName } = useCalendar();

  const itemStart = startOfDay(parseISO(event.startDate));
  const itemEnd = endOfDay(parseISO(event.endDate));

  if (cellDate < itemStart || cellDate > itemEnd) return null;

  const position: "first" | "middle" | "last" | "none" =
    propPosition ??
    (eventCurrentDay && eventTotalDays
      ? "none"
      : isSameDay(itemStart, itemEnd)
        ? "none"
        : isSameDay(cellDate, itemStart)
          ? "first"
          : isSameDay(cellDate, itemEnd)
            ? "last"
            : "middle");

  const renderBadgeText = ["first", "none"].includes(position);

  const color: VariantProps<typeof eventBadgeVariants>["color"] =
    badgeVariant === "dot" ? `${event.color}-dot` : event.color;

  // Planned ghost copy (MOR-696 "both"/"planned" mode): render dashed + faded
  // so it reads as a soft schedule marker, distinct from the hard due badge.
  const isPlannedMarker = event.meta?.marker === "planned";

  const classes = cn(
    eventBadgeVariants({ color, multiDayPosition: position }),
    isPlannedMarker && "border-dashed bg-transparent opacity-70",
    className,
  );

  const handleClick = () => onEventClick(event);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEventClick(event);
    }
  };

  // The badge sits on the due date; surface the soft planned target too so the
  // distinction is visible in the month grid (where there's no column for it).
  const planned = event.meta?.plannedDate ?? null;
  const due = event.meta?.dueDate ?? null;
  const showPlanned = !!planned && planned !== due;
  const tooltip = [
    due ? `Due ${format(parseISO(due), "d MMM yyyy")}` : null,
    planned ? `Planned ${format(parseISO(planned), "d MMM yyyy")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes}
      title={tooltip || event.title}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {showJobName && renderBadgeText && event.meta?.jobName && (
          <span className="truncate text-[10px] font-medium opacity-60">
            {event.meta.jobName}
          </span>
        )}

        <div className="flex min-w-0 items-center gap-1.5">
          {!["middle", "last"].includes(position) &&
            ["mixed", "dot"].includes(badgeVariant) && (
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                className="event-dot shrink-0"
              >
                <circle cx="4" cy="4" r="4" />
              </svg>
            )}

          {renderBadgeText && (
            <p
              className={cn(
                "min-w-0 flex-1 font-semibold",
                showJobName ? "truncate" : "line-clamp-2",
              )}
              title={showJobName ? event.title : undefined}
            >
              {isPlannedMarker && (
                <span className="mr-0.5 text-[10px] font-medium uppercase tracking-wide opacity-70">
                  Plan ·{" "}
                </span>
              )}
              {eventCurrentDay && eventTotalDays && eventTotalDays > 1 && (
                <span className="mr-0.5 text-[10px] opacity-70">
                  Day {eventCurrentDay}/{eventTotalDays} •{" "}
                </span>
              )}
              {event.title}
            </p>
          )}

          {renderBadgeText && !event.meta?.allDay && (
            <span className="shrink-0 text-[10px] opacity-70">
              {format(new Date(event.startDate), "h:mm a")}
            </span>
          )}
        </div>

        {renderBadgeText && showPlanned && planned && !isPlannedMarker && (
          <span className="inline-flex w-fit items-center rounded border border-dashed border-current px-1 text-[9px] opacity-70">
            Plan {format(parseISO(planned), "d MMM")}
          </span>
        )}
      </div>
    </div>
  );
}
